import * as THREE from 'three';
import {
  ANIMATION_BLOCK_VIEW_ID,
  RANDOM_BLOCK_VIEW_ID,
  TEXTURE_GRID_SIZE,
  blockTransformMatrix,
  currentWorldAnimTick,
  isParametricAnimation,
  libraryBlockId,
  resolveParametricSegment,
  resolvePlacementDisplayViewId,
  textureGridSizeFromPixels,
  type BlockNoiseParams,
  type LibraryBlockDefinition,
  type LibraryBlockViewSnapshot,
  type TextureGridSize,
  type TexturePixels,
} from '@shared/protocol';
import {
  applyNoiseToTexturePixels,
  isNoiseActive,
  resolveNoiseSeed,
} from '@shared/blockNoise';
import type { GlowLightManager } from './GlowLightManager';
import {
  AnimationTickGate,
  type AnimationFlags,
} from './animationControl';

interface Placement {
  x: number;
  y: number;
  z: number;
  id: number;
  transform: number;
  viewId: number;
  animOrigin: number;
  displayViewId: number;
  meshKey: string;
}

type LibraryAppearance =
  | {
      kind: 'solid';
      seedKey: number;
      color: string;
      opacity: number;
      glowIntensity: number | null;
    }
  | {
      kind: 'textured';
      seedKey: number;
      color: string;
      opacity: number;
      gridSize: TextureGridSize;
      pixels: TexturePixels;
      noise: BlockNoiseParams | null;
    };

/** Instanced fixed-cube renderer for user-created color and texture blocks. */
export class LibraryBlockRenderer {
  readonly group = new THREE.Group();

  private readonly definitions = new Map<number, LibraryBlockDefinition>();
  private readonly placements = new Map<string, Placement>();
  private readonly meshes = new Map<string, THREE.Mesh | THREE.InstancedMesh>();
  private readonly dirty = new Set<string>();
  private readonly animGate = new AnimationTickGate();
  private worldTick = currentWorldAnimTick();
  private glowVisuals = true;

  constructor(private readonly glowLights: GlowLightManager) {}

  /** When false, solids use lit materials and point lights are cleared. */
  setGlowVisualsEnabled(enabled: boolean): void {
    if (this.glowVisuals === enabled) return;
    this.glowVisuals = enabled;
    for (const key of this.meshes.keys()) this.dirty.add(key);
    for (const placement of this.placements.values()) {
      this.dirty.add(placement.meshKey);
    }
  }

  setAnimationFlags(flags: AnimationFlags): void {
    if (!this.animGate.setFlags(flags, this.worldTick)) return;
    this.dirtyAnimatedPlacements();
  }

  addDefinitions(definitions: LibraryBlockDefinition[]): void {
    for (const definition of definitions) {
      this.definitions.set(definition.id, definition);
      this.refreshDefinitionDisplays(definition.id);
      this.markDefinitionDirty(definition.id);
    }
  }

  setCell(
    x: number,
    y: number,
    z: number,
    type: number,
    transform = 0,
    viewId = 0,
    animOrigin = 0,
  ): void {
    const key = `${x},${y},${z}`;
    const previous = this.placements.get(key);
    if (previous) {
      this.placements.delete(key);
      this.glowLights.removeSource(`lib:${previous.x},${previous.y},${previous.z}`);
      this.markDefinitionDirty(previous.id);
    }
    const id = libraryBlockId(type);
    if (id !== null) {
      const definition = this.definitions.get(id);
      const displayViewId = definition
        ? resolvePlacementDisplayViewId(
          definition.activeViewId,
          definition.animation,
          viewId,
          animOrigin,
          this.worldTick,
        )
        : viewId;
      const meshKey = meshKeyFor(id, viewId, animOrigin, displayViewId, definition);
      this.placements.set(key, {
        x, y, z, id, transform, viewId, animOrigin, displayViewId, meshKey,
      });
      this.markDefinitionDirty(id);
    }
  }

  setAnimationTick(worldTick: number): void {
    if (worldTick === this.worldTick) return;
    this.worldTick = worldTick;
    for (const placement of this.placements.values()) {
      const definition = this.definitions.get(placement.id);
      if (!definition) continue;
      const tick = this.animGate.tickFor(definition.animation, worldTick);
      const next = resolvePlacementDisplayViewId(
        definition.activeViewId,
        definition.animation,
        placement.viewId,
        placement.animOrigin,
        tick,
      );
      const nextKey = meshKeyFor(
        placement.id,
        placement.viewId,
        placement.animOrigin,
        next,
        definition,
      );
      const parametricFollow = isParametricFollow(definition, placement.viewId)
        && this.animGate.isKindEnabled('parametric');
      if (
        next === placement.displayViewId
        && nextKey === placement.meshKey
        && !parametricFollow
      ) {
        continue;
      }
      this.dirty.add(placement.meshKey);
      placement.displayViewId = next;
      placement.meshKey = nextKey;
      this.dirty.add(nextKey);
    }
  }

  update(): void {
    for (const key of this.dirty) this.rebuild(key);
    this.dirty.clear();
  }

  /**
   * Neighbor face culling is definition-wide (all snapshots). Changing any
   * placement can expose/hide faces on every view-variant mesh of that block.
   */
  private markDefinitionDirty(id: number): void {
    for (const meshKey of this.meshes.keys()) {
      if (Number(meshKey.split(':')[0]) === id) this.dirty.add(meshKey);
    }
    for (const placement of this.placements.values()) {
      if (placement.id === id) this.dirty.add(placement.meshKey);
    }
  }

  private refreshDefinitionDisplays(id: number): void {
    const definition = this.definitions.get(id);
    if (!definition) return;
    for (const placement of this.placements.values()) {
      if (placement.id !== id) continue;
      const tick = this.animGate.tickFor(definition.animation, this.worldTick);
      placement.displayViewId = resolvePlacementDisplayViewId(
        definition.activeViewId,
        definition.animation,
        placement.viewId,
        placement.animOrigin,
        tick,
      );
      placement.meshKey = meshKeyFor(
        placement.id,
        placement.viewId,
        placement.animOrigin,
        placement.displayViewId,
        definition,
      );
    }
  }

  private dirtyAnimatedPlacements(): void {
    for (const placement of this.placements.values()) {
      const definition = this.definitions.get(placement.id);
      if (!definition?.animation) continue;
      const tick = this.animGate.tickFor(definition.animation, this.worldTick);
      const next = resolvePlacementDisplayViewId(
        definition.activeViewId,
        definition.animation,
        placement.viewId,
        placement.animOrigin,
        tick,
      );
      const nextKey = meshKeyFor(
        placement.id,
        placement.viewId,
        placement.animOrigin,
        next,
        definition,
      );
      this.dirty.add(placement.meshKey);
      placement.displayViewId = next;
      placement.meshKey = nextKey;
      this.dirty.add(nextKey);
    }
  }

  private rebuild(key: string): void {
    this.removeMesh(key);
    const id = Number(key.split(':')[0]);
    const definition = this.definitions.get(id);
    const allPlacements = [...this.placements.values()].filter((item) => item.id === id);
    const placements = allPlacements.filter((item) => item.meshKey === key);
    if (placements.length === 0 || !definition) return;
    const sample = placements[0]!;
    const appearance = resolveLibraryAppearance(
      definition,
      sample.displayViewId,
      sample.viewId,
      sample.animOrigin,
      this.animGate.tickFor(definition.animation, this.worldTick),
    );
    // Cull against every placement of this definition, including other snapshots,
    // so Random-mode neighbors don't keep shared faces (transparent sort flicker).
    if (appearance.kind === 'solid') {
      const glowing = this.glowVisuals && (appearance.glowIntensity ?? 0) > 0;
      const mesh = new THREE.Mesh(
        buildSolidSurfaceGeometry(placements, allPlacements),
        solidMaterial(appearance, this.glowVisuals),
      );
      mesh.castShadow = !glowing && appearance.opacity >= 1;
      mesh.receiveShadow = !glowing;
      if (appearance.opacity < 1 || glowing) mesh.renderOrder = glowing ? 2 : 1;
      this.meshes.set(key, mesh);
      this.group.add(mesh);
      for (const placement of placements) {
        const lightKey = `lib:${placement.x},${placement.y},${placement.z}`;
        if (glowing) {
          this.glowLights.setSource(
            lightKey,
            placement.x,
            placement.y,
            placement.z,
            appearance.color,
            appearance.glowIntensity!,
          );
        } else {
          this.glowLights.removeSource(lightKey);
        }
      }
      return;
    }
    const mesh = new THREE.Mesh(
      buildTexturedSurfaceGeometry(placements, allPlacements),
      materialsFor(appearance),
    );
    mesh.castShadow = isAppearanceOpaque(appearance);
    mesh.receiveShadow = true;
    if (!isAppearanceOpaque(appearance)) mesh.renderOrder = 1;
    this.meshes.set(key, mesh);
    this.group.add(mesh);
  }

  private removeMesh(key: string): void {
    const mesh = this.meshes.get(key);
    if (!mesh) return;
    this.group.remove(mesh);
    mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      const mapped = material as THREE.MeshLambertMaterial;
      mapped.map?.dispose?.();
      material.dispose();
    }
    if (mesh instanceof THREE.InstancedMesh) mesh.dispose();
    this.meshes.delete(key);
  }
}

function isParametricFollow(
  definition: LibraryBlockDefinition,
  placementViewId: number,
): boolean {
  return placementViewId <= RANDOM_BLOCK_VIEW_ID
    && definition.activeViewId === ANIMATION_BLOCK_VIEW_ID
    && isParametricAnimation(definition.animation);
}

function meshKeyFor(
  id: number,
  placementViewId: number,
  animOrigin: number,
  displayViewId: number,
  definition: LibraryBlockDefinition | undefined,
): string {
  if (definition && isParametricFollow(definition, placementViewId)) {
    return definition.animation.sync
      ? `${id}:param`
      : `${id}:param:${animOrigin}`;
  }
  return `${id}:${displayViewId}`;
}

function resolveLibraryAppearance(
  definition: LibraryBlockDefinition,
  displayViewId: number,
  placementViewId: number,
  animOrigin: number,
  worldTick: number,
): LibraryAppearance {
  if (isParametricFollow(definition, placementViewId) && definition.kind === 'textured') {
    const blended = resolveParametricTexturedAppearance(
      definition,
      animOrigin,
      worldTick,
    );
    if (blended) return blended;
  }
  if (displayViewId > RANDOM_BLOCK_VIEW_ID) {
    const snapshot = definition.snapshots?.find((item) => item.id === displayViewId);
    if (snapshot) return appearanceFromSnapshot(snapshot);
  }
  if (definition.kind === 'solid') {
    return {
      kind: 'solid',
      seedKey: definition.id,
      color: definition.color,
      opacity: definition.opacity,
      glowIntensity: definition.glowIntensity,
    };
  }
  return {
    kind: 'textured',
    seedKey: definition.id,
    color: definition.color,
    opacity: definition.opacity,
    gridSize: definition.gridSize,
    pixels: definition.pixels,
    noise: definition.noise,
  };
}

/** Lerp solid-fill color / opacity / noise between parametric keyframes. */
function resolveParametricTexturedAppearance(
  definition: Extract<LibraryBlockDefinition, { kind: 'textured' }>,
  animOrigin: number,
  worldTick: number,
): Extract<LibraryAppearance, { kind: 'textured' }> | null {
  if (!isParametricAnimation(definition.animation)) return null;
  const segment = resolveParametricSegment(
    definition.animation,
    worldTick,
    animOrigin,
  );
  if (!segment) return null;
  const from = definition.snapshots?.find((item) => item.id === segment.fromViewId);
  const to = definition.snapshots?.find((item) => item.id === segment.toViewId);
  if (!from || from.kind !== 'textured' || !to || to.kind !== 'textured') return null;
  const t = segment.t;
  const color = lerpHexColor(from.color, to.color, t);
  const opacity = lerpNumber(from.opacity, to.opacity, t);
  const gridSize = from.gridSize;
  const pixels = solidFillTexturePixels(color, opacity, gridSize);
  const noise = lerpNoiseParams(from.noise, to.noise, t);
  return {
    kind: 'textured',
    seedKey: definition.id,
    color,
    opacity,
    gridSize,
    pixels,
    noise,
  };
}

function lerpNumber(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpHexColor(a: string, b: string, t: number): string {
  const ar = Number.parseInt(a.slice(1, 3), 16);
  const ag = Number.parseInt(a.slice(3, 5), 16);
  const ab = Number.parseInt(a.slice(5, 7), 16);
  const br = Number.parseInt(b.slice(1, 3), 16);
  const bg = Number.parseInt(b.slice(3, 5), 16);
  const bb = Number.parseInt(b.slice(5, 7), 16);
  const r = Math.round(lerpNumber(ar, br, t));
  const g = Math.round(lerpNumber(ag, bg, t));
  const bl = Math.round(lerpNumber(ab, bb, t));
  return `#${[r, g, bl].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

function lerpNoiseParams(
  from: BlockNoiseParams | null,
  to: BlockNoiseParams | null,
  t: number,
): BlockNoiseParams | null {
  if (!from && !to) return null;
  const a = from ?? to!;
  const b = to ?? from!;
  return {
    type: t < 0.5 ? a.type : b.type,
    blend: t < 0.5 ? a.blend : b.blend,
    intensity: Math.round(lerpNumber(a.intensity, b.intensity, t)),
    seed: Math.round(lerpNumber(a.seed ?? 0, b.seed ?? 0, t)),
  };
}

function solidFillTexturePixels(
  color: string,
  opacity: number,
  gridSize: TextureGridSize,
): TexturePixels {
  const alpha = Math.max(0, Math.min(255, Math.round(opacity * 255)))
    .toString(16)
    .padStart(2, '0');
  const fill = `${color}${alpha}`;
  const cellCount = gridSize * gridSize;
  return Array.from({ length: 6 }, () =>
    Array.from({ length: cellCount }, () => fill));
}

function appearanceFromSnapshot(snapshot: LibraryBlockViewSnapshot): LibraryAppearance {
  if (snapshot.kind === 'solid') {
    return {
      kind: 'solid',
      seedKey: snapshot.id,
      color: snapshot.color,
      opacity: snapshot.opacity,
      glowIntensity: snapshot.glowIntensity ?? null,
    };
  }
  return {
    kind: 'textured',
    seedKey: snapshot.id,
    color: snapshot.color,
    opacity: snapshot.opacity,
    gridSize: snapshot.gridSize,
    pixels: snapshot.pixels,
    noise: snapshot.noise,
  };
}

function solidMaterial(
  appearance: Extract<LibraryAppearance, { kind: 'solid' }>,
  glowVisuals = true,
): THREE.MeshLambertMaterial | THREE.MeshBasicMaterial {
  const glowing = glowVisuals && (appearance.glowIntensity ?? 0) > 0;
  if (glowing) {
    // Unlit self-colored mesh — no shadow casting / receiving (cheap).
    return new THREE.MeshBasicMaterial({
      color: appearance.color,
      opacity: appearance.opacity,
      transparent: appearance.opacity < 1,
      depthWrite: appearance.opacity >= 1,
      toneMapped: false,
    });
  }
  return new THREE.MeshLambertMaterial({
    color: appearance.color,
    opacity: appearance.opacity,
    transparent: appearance.opacity < 1,
    depthWrite: appearance.opacity >= 1,
  });
}

function materialsFor(appearance: LibraryAppearance): THREE.MeshLambertMaterial[] {
  if (appearance.kind === 'solid') {
    return Array.from(
      { length: 6 },
      () => new THREE.MeshLambertMaterial({
        color: appearance.color,
        opacity: appearance.opacity,
        transparent: appearance.opacity < 1,
        depthWrite: appearance.opacity >= 1,
      }),
    );
  }
  const size = textureGridSizeFromPixels(appearance.pixels)
    ?? appearance.gridSize
    ?? TEXTURE_GRID_SIZE;
  const pixels = isNoiseActive(appearance.noise)
    ? applyNoiseToTexturePixels(
      appearance.pixels,
      appearance.noise,
      resolveNoiseSeed(appearance.noise, appearance.seedKey),
    )
    : appearance.pixels;
  return pixels.map((face) => {
    const data = new Uint8Array(size * size * 4);
    face.forEach((color, index) => {
      const [r, g, b, a] = rgbaBytes(color);
      data[index * 4] = r;
      data[index * 4 + 1] = g;
      data[index * 4 + 2] = b;
      data[index * 4 + 3] = a;
    });
    const texture = new THREE.DataTexture(
      data,
      size,
      size,
      THREE.RGBAFormat,
    );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    const transparent = face.some((color) => rgbaBytes(color)[3] < 255);
    return new THREE.MeshLambertMaterial({
      map: texture,
      transparent,
      depthWrite: !transparent,
    });
  });
}

function isAppearanceOpaque(appearance: LibraryAppearance): boolean {
  return appearance.kind === 'solid'
    ? appearance.opacity >= 1
    : appearance.pixels.every((face) =>
        face.every((color) => rgbaBytes(color)[3] === 255));
}

function buildSolidSurfaceGeometry(
  emitPlacements: Placement[],
  occupiedPlacements: Placement[] = emitPlacements,
): THREE.BufferGeometry {
  const occupied = new Set(
    occupiedPlacements.map((item) => `${item.x},${item.y},${item.z}`),
  );
  const planes = new Map<string, {
    d: number;
    u: number;
    v: number;
    normal: 1 | -1;
    plane: number;
    cells: Set<string>;
  }>();
  for (const placement of emitPlacements) {
    const coordinates = [placement.x, placement.y, placement.z];
    for (let d = 0; d < 3; d++) {
      const u = (d + 1) % 3;
      const v = (d + 2) % 3;
      for (const normal of [-1, 1] as const) {
        const neighbor = [...coordinates];
        neighbor[d] += normal;
        if (occupied.has(neighbor.join(','))) continue;
        const plane = coordinates[d] + (normal > 0 ? 1 : 0);
        const key = `${d}:${normal}:${plane}`;
        let group = planes.get(key);
        if (!group) {
          group = { d, u, v, normal, plane, cells: new Set() };
          planes.set(key, group);
        }
        group.cells.add(`${coordinates[u]},${coordinates[v]}`);
      }
    }
  }

  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  for (const group of planes.values()) {
    const remaining = new Set(group.cells);
    const ordered = [...group.cells]
      .map((cell) => cell.split(',').map(Number) as [number, number])
      .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
    for (const [startU, startV] of ordered) {
      if (!remaining.has(`${startU},${startV}`)) continue;
      let width = 1;
      while (remaining.has(`${startU + width},${startV}`)) width++;
      let height = 1;
      heightLoop: while (true) {
        for (let offset = 0; offset < width; offset++) {
          if (!remaining.has(`${startU + offset},${startV + height}`)) {
            break heightLoop;
          }
        }
        height++;
      }
      for (let row = 0; row < height; row++) {
        for (let column = 0; column < width; column++) {
          remaining.delete(`${startU + column},${startV + row}`);
        }
      }
      emitSolidQuad(
        group, startU, startV, width, height,
        positions, normals, indices,
      );
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function emitSolidQuad(
  group: {
    d: number;
    u: number;
    v: number;
    normal: 1 | -1;
    plane: number;
  },
  startU: number,
  startV: number,
  width: number,
  height: number,
  positions: number[],
  normals: number[],
  indices: number[],
): void {
  const base = positions.length / 3;
  const point = [0, 0, 0];
  point[group.d] = group.plane;
  point[group.u] = startU;
  point[group.v] = startV;
  const du = [0, 0, 0];
  const dv = [0, 0, 0];
  du[group.u] = width;
  dv[group.v] = height;
  const add = (a: number[], b: number[]) => a.map((value, index) => value + b[index]);
  const corners = group.normal > 0
    ? [point, add(point, du), add(add(point, du), dv), add(point, dv)]
    : [point, add(point, dv), add(add(point, du), dv), add(point, du)];
  const normal = [0, 0, 0];
  normal[group.d] = group.normal;
  for (const corner of corners) {
    positions.push(corner[0], corner[1], corner[2]);
    normals.push(normal[0], normal[1], normal[2]);
  }
  indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

/** Local cube faces in 0..1 space (BoxGeometry material order: +X,-X,+Y,-Y,+Z,-Z). */
const TEXTURED_FACES: ReadonlyArray<{
  material: number;
  normal: readonly [number, number, number];
  /** Outward CCW corners in unit-cube space. */
  corners: ReadonlyArray<readonly [number, number, number]>;
  uvs: ReadonlyArray<readonly [number, number]>;
}> = [
  {
    material: 0,
    normal: [1, 0, 0],
    corners: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]],
    uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
  },
  {
    material: 1,
    normal: [-1, 0, 0],
    corners: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]],
    uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
  },
  {
    material: 2,
    normal: [0, 1, 0],
    corners: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]],
    uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
  },
  {
    material: 3,
    normal: [0, -1, 0],
    corners: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]],
    uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
  },
  {
    material: 4,
    normal: [0, 0, 1],
    corners: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]],
    uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
  },
  {
    material: 5,
    normal: [0, 0, -1],
    corners: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]],
    uvs: [[0, 0], [1, 0], [1, 1], [0, 1]],
  },
];

/**
 * Exposed-face mesh for textured library cubes. Skips faces shared with another
 * placement of the same definition (any snapshot), including semi-transparent glass.
 */
function buildTexturedSurfaceGeometry(
  emitPlacements: Placement[],
  occupiedPlacements: Placement[] = emitPlacements,
): THREE.BufferGeometry {
  const occupied = new Set(
    occupiedPlacements.map((item) => `${item.x},${item.y},${item.z}`),
  );
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const groups: Array<{ material: number; start: number; count: number }> = [];

  for (let material = 0; material < 6; material++) {
    const indexStart = indices.length;
    const face = TEXTURED_FACES[material]!;
    for (const placement of emitPlacements) {
      const matrix = blockTransformMatrix(placement.transform);
      const [lnx, lny, lnz] = face.normal;
      const wnx = Math.round(matrix[0]! * lnx + matrix[1]! * lny + matrix[2]! * lnz);
      const wny = Math.round(matrix[3]! * lnx + matrix[4]! * lny + matrix[5]! * lnz);
      const wnz = Math.round(matrix[6]! * lnx + matrix[7]! * lny + matrix[8]! * lnz);
      const neighborKey = `${placement.x + wnx},${placement.y + wny},${placement.z + wnz}`;
      if (occupied.has(neighborKey)) continue;

      const det = matrixDeterminant(matrix);
      const corners = face.corners.map(([x, y, z]) => {
        const [tx, ty, tz] = transformUnitPoint(matrix, x, y, z);
        return [
          placement.x + tx,
          placement.y + ty,
          placement.z + tz,
        ] as const;
      });
      const ordered = det < 0
        ? [corners[0]!, corners[3]!, corners[2]!, corners[1]!]
        : corners;
      const orderedUvs = det < 0
        ? [face.uvs[0]!, face.uvs[3]!, face.uvs[2]!, face.uvs[1]!]
        : face.uvs;
      const base = positions.length / 3;
      for (let i = 0; i < 4; i++) {
        const corner = ordered[i]!;
        const uv = orderedUvs[i]!;
        positions.push(corner[0], corner[1], corner[2]);
        normals.push(wnx, wny, wnz);
        uvs.push(uv[0], uv[1]);
      }
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    const count = indices.length - indexStart;
    if (count > 0) groups.push({ material, start: indexStart, count });
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  for (const group of groups) {
    geometry.addGroup(group.start, group.count, group.material);
  }
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/** Apply placement transform the same way as `transformedCubeGeometry` (center → M → +0.5). */
function transformUnitPoint(
  matrix: readonly number[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  const cx = x - 0.5;
  const cy = y - 0.5;
  const cz = z - 0.5;
  return [
    matrix[0]! * cx + matrix[1]! * cy + matrix[2]! * cz + 0.5,
    matrix[3]! * cx + matrix[4]! * cy + matrix[5]! * cz + 0.5,
    matrix[6]! * cx + matrix[7]! * cy + matrix[8]! * cz + 0.5,
  ];
}

function matrixDeterminant(matrix: readonly number[]): number {
  const a = matrix[0]!;
  const b = matrix[1]!;
  const c = matrix[2]!;
  const d = matrix[3]!;
  const e = matrix[4]!;
  const f = matrix[5]!;
  const g = matrix[6]!;
  const h = matrix[7]!;
  const i = matrix[8]!;
  return a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
}

function rgbaBytes(color: string): [number, number, number, number] {
  const hex = color.slice(1);
  const value = Number.parseInt(hex, 16);
  return hex.length === 8
    ? [(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255]
    : [(value >> 16) & 255, (value >> 8) & 255, value & 255, 255];
}
